import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import { motion } from 'framer-motion';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
	variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
	size?: 'sm' | 'md' | 'lg';
	fullWidth?: boolean;
	leftIcon?: ReactNode;
	rightIcon?: ReactNode;
	isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = ({
	children,
	variant = 'primary',
	size = 'md',
	fullWidth = false,
	leftIcon,
	rightIcon,
	isLoading = false,
	className = '',
	type = 'button',
	...props
}) => {
	// Base classes
	const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer';

	// Size classes
	const sizeClasses = {
		sm: 'text-sm px-3 py-1.5',
		md: 'text-base px-4 py-2',
		lg: 'text-lg px-6 py-3',
	};

	// Variant classes
	const variantClasses = {
		primary: 'bg-primary text-white hover:bg-primary-dark',
		secondary: 'bg-secondary text-white hover:bg-secondary-dark',
		outline: `border ${className ? '' : 'border-primary text-primary hover:bg-primary/10 dark:border-primary dark:text-primary'}`,
		ghost: 'text-primary hover:bg-primary/10 dark:text-primary'
	};

	// Width class
	const widthClass = fullWidth ? 'w-full' : '';

	const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${className}`;

	return (
		<motion.button
			whileTap={{ scale: 0.98 }}
			className={classes}
			type={type}
			disabled={isLoading || props.disabled}
			{...(props as React.ComponentPropsWithoutRef<typeof motion.button>)}
		>
			{isLoading && (
				<svg className="animate-spin -ms-1 me-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
					<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
					<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
			)}

			{!isLoading && leftIcon && <span className="me-2">{leftIcon}</span>}
			<span>{children}</span>
			{!isLoading && rightIcon && <span className="ms-2">{rightIcon}</span>}
		</motion.button>
	);
};

export default Button;